import { Button } from "antd";

import { Filter } from "../pages/Dashboard";
import { AssigneeCounts } from "../store/invoicesSlice";
import { useAppSelector } from "../store";

/** `assignedTo` filter value that selects documents nobody is assigned to (understood by the API). */
export const UNASSIGNED_FILTER = "unassigned";
export const UNASSIGNED_LABEL = "Unassigned";

interface Props {
  counts: AssigneeCounts;
  filter: Filter;
  onUserChange: (value: { value: string; label: string } | null) => void;
}

/**
 * The Assigned To filter as a row of buttons with counts, like the Status row: All, Unassigned and
 * one button per person who has documents in the current selection. The counts honour the other
 * filters (status tab, document type, service team, search) so each number is the rows you get by
 * clicking it.
 */
function AssigneeFilters({ counts, filter, onUserChange }: Props) {
  const loadingInvoices = useAppSelector((state) => state.invoices.loading);
  const selected = filter?.assignedTo ?? "";

  const assignees = [...(counts?.assignees ?? [])];
  // Keep the selected person visible even when nothing matches the other filters, so the filter can be turned off.
  if (selected && selected !== UNASSIGNED_FILTER && !assignees.some((a) => a.id === selected)) {
    assignees.push({ id: selected, name: filter?.assignedToName || selected, count: 0 });
  }

  const buttonClass = (isSelected: boolean) => `me-5px fs-12px mb-4px bg-gray ${isSelected ? "selected" : ""}`;

  return (
    <div className="assignee-filters">
      <Button size="small" className={buttonClass(!selected)} onClick={() => onUserChange(null)} disabled={loadingInvoices}>
        All {counts?.total ?? 0}
      </Button>
      <Button
        size="small"
        className={buttonClass(selected === UNASSIGNED_FILTER)}
        onClick={() => onUserChange({ value: UNASSIGNED_FILTER, label: UNASSIGNED_LABEL })}
        disabled={loadingInvoices}
      >
        {UNASSIGNED_LABEL} {counts?.unassigned ?? 0}
      </Button>
      {assignees.map((assignee) => (
        <Button
          key={assignee.id}
          size="small"
          className={buttonClass(selected === assignee.id)}
          onClick={() => onUserChange({ value: assignee.id, label: assignee.name })}
          disabled={loadingInvoices}
        >
          {assignee.name} {assignee.count}
        </Button>
      ))}
    </div>
  );
}

export default AssigneeFilters;
